import React from "react";
import { Card, CardHeader, CardTitle, Splitter, SplitterPane, SplitterOnChangeEvent, TabStrip, TabStripSelectEventArguments, TabStripTab } from "@progress/kendo-react-layout";
import { Loader } from "@progress/kendo-react-indicators";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { useFastaStore } from "@presentation/stores/FastaStore";
import { useFileExplorerStore } from "@presentation/stores/FileExplorerStore";
import { useInjection } from "@presentation/components/dependencyInjection";
import { LibraryRepository } from "@infrastructure/repositories/LibraryRepository";
import { FastaFile, FileMetadata, JobPipelineTypeSchema, SettingsTypeEnumSchema, SoftwareSettingsMetadata, StatusEnumSchema } from "@domain/schemas/BI";
import { useLocation } from "react-router-dom";
import { ListJobsService } from "@application/useCases/job/ListJobTasksService";
import { ListFastaService, ListSoftwareSettingsService } from "@application/useCases";

import JobSetupForm from "@presentation/components/job/JobSetupForm";
import "@presentation/pages/styles/JobSetupPage.scss";
import { FileExplorerGrid } from "./FileExplorerPage";
import { LibraryExplorerGrid } from "@presentation/components/inventory/LibraryExplorerGrid";
import { ArchiveExplorerGrid, ArchiveGridItem } from "@presentation/components/inventory/ArchiveExplorerGrid";
import { ArchiveRepository } from "@infrastructure/repositories/ArchiveRepository";

const PULSAR_STEP_1_ID = "22222222-2222-2222-2222-222222222222";
const LIBRARY_GENERATION_ID = "55555555-5555-5555-5555-555555555555";

const CreateJobFormCard = () => {
  const location = useLocation();
  const injectedListJobsService = useInjection(ListJobsService);
  const injectedListFastaService = useInjection(ListFastaService);
  const injectedListSoftwareSettingsService = useInjection(ListSoftwareSettingsService);
  const listJobsServiceRef = React.useRef(injectedListJobsService);
  const listFastaServiceRef = React.useRef(injectedListFastaService);
  const listSoftwareSettingsServiceRef = React.useRef(injectedListSoftwareSettingsService);
  const { loadExistingJobForRerun, resetCreateJobState } = useJobSetupStore((state) => ({
    loadExistingJobForRerun: state.loadExistingJobForRerun,
    setRerunAvailableSteps: state.setRerunAvailableSteps,
    resetCreateJobState: state.resetCreateJobState,
  }));

  React.useEffect(() => {
    let isCancelled = false;

    const loadMergeNodePools = async () => {
      const store = useJobSetupStore.getState();
      store.setMergeNodePoolOptionsLoading(true);
      store.setMergeNodePoolOptionsError(null);

      try {
        const options = await listJobsServiceRef.current.getAvailableMergeNodePoolsAsync();
        if (!isCancelled) {
          store.setMergeNodePoolOptions(options);
        }
      } catch (error) {
        console.error("Failed to load merge node pool options", error);
        if (!isCancelled) {
          store.setMergeNodePoolOptions([]);
          store.setMergeNodePoolOptionsError("Could not load merge node pool options. Default merge pool will be used.");
        }
      } finally {
        if (!isCancelled) {
          store.setMergeNodePoolOptionsLoading(false);
        }
      }
    };

    loadMergeNodePools();

    return () => {
      isCancelled = true;
    };
  }, []);

  React.useEffect(() => {
    const normalizePath = (path?: string | null) => (path ?? "").replace(/\\/g, "/").replace(/^\/+|\/+$/g, "");
    const toFullPath = (location?: string | null, name?: string | null) => normalizePath([location, name].filter(Boolean).join("/"));
    const toLocationAndName = (path: string) => {
      const normalized = normalizePath(path);
      const parts = normalized.split("/").filter(Boolean);
      return {
        location: parts.slice(0, -1).join("/"),
        name: parts[parts.length - 1] ?? normalized,
      };
    };

    const toFallbackFasta = (path: string, createdBy: string): FastaFile => {
      const { location, name } = toLocationAndName(path);
      return {
        createdBy,
        location,
        fileName: name,
        size: 0,
      };
    };

    const toFallbackSetting = (path: string, createdBy: string, softwareVersion: string, settingsType: SettingsTypeEnumSchema): SoftwareSettingsMetadata => {
      const { location, name } = toLocationAndName(path);
      return {
        id: normalizePath(path),
        name,
        location,
        createdBy,
        software: "Spectronaut",
        softwareVersion,
        settingsType,
      } as SoftwareSettingsMetadata;
    };

    const searchParams = new URLSearchParams(location.search);
    const rerunJobId = searchParams.get("rerunJobId");

    if (!rerunJobId) {
      resetCreateJobState();
      useFastaStore.getState().setSelectedFasta([]);
      useFileExplorerStore.getState().setSelectedState({});
      return;
    }

    let isCancelled = false;

    const loadJob = async () => {
      try {
        const job = await listJobsServiceRef.current.getJobByIDAsync(rerunJobId);

        if (!isCancelled) {
          loadExistingJobForRerun(job);
          useFastaStore.getState().setSelectedFasta(
            (job.fastaFiles ?? []).map((path) => toFallbackFasta(path, job.createdBy ?? "")),
          );
          const stepDetails = await listJobsServiceRef.current.getJobStepListAsync(rerunJobId);
          if (isCancelled) return;
          useJobSetupStore.getState().setRerunAvailableSteps(stepDetails, job.status !== StatusEnumSchema.Completed);

          const sourceParallelStep = stepDetails
            .filter((step) => step.isParallel && (step.batchesCount ?? 0) > 1)
            .sort((a, b) => a.position - b.position)[0];

          if (sourceParallelStep) {
            const batchResponse = await listJobsServiceRef.current.getJobStepBatchListAsync(rerunJobId, sourceParallelStep.id);
            if (isCancelled) return;

            const inferredBatchSize = batchResponse.list?.[0]?.rawFileIds?.length;
            if (typeof inferredBatchSize === "number" && inferredBatchSize > 0) {
              useJobSetupStore.getState().setBatchSize(inferredBatchSize);
            }
          }

          const [fastaResponse, experimentResponse, conditionResponse, modificationResponse, reportResponse, goResponse] = await Promise.all([
            listFastaServiceRef.current.listFastaAsync(),
            listSoftwareSettingsServiceRef.current.getSettingsListAsync(SettingsTypeEnumSchema.Experiment),
            listSoftwareSettingsServiceRef.current.getSettingsListAsync(SettingsTypeEnumSchema.ConditionSetup),
            listSoftwareSettingsServiceRef.current.getSettingsListAsync(SettingsTypeEnumSchema.Modification),
            listSoftwareSettingsServiceRef.current.getSettingsListAsync(SettingsTypeEnumSchema.Report),
            listSoftwareSettingsServiceRef.current.getSettingsListAsync(SettingsTypeEnumSchema.GO),
          ]);

          if (isCancelled) return;

          const fastaList = fastaResponse.list ?? [];
          useFastaStore.getState().setFastaList(fastaList);
          const fastaByPath = new Map(fastaList.map((item) => [toFullPath(item.location, item.fileName), item]));

          const selectedFasta = (job.fastaFiles ?? []).map((path) => {
            const normalizedPath = normalizePath(path);
            return fastaByPath.get(normalizedPath)
              ?? toFallbackFasta(path, job.createdBy ?? "");
          });
          useFastaStore.getState().setSelectedFasta(selectedFasta);
          useFileExplorerStore.getState().setSelectedState(
            Object.fromEntries(
              (job.rawFiles ?? []).map((path) => {
                const normalizedPath = normalizePath(path);
                return [normalizedPath, { path: normalizedPath, sizeInBytes: 0, isFolder: false }];
              }),
            ),
          );

          const jobSetupStore = useJobSetupStore.getState();

          const preserveSingleSelection = (
            sourcePath: string | null | undefined,
            currentSelection: SoftwareSettingsMetadata | null,
            resolvedSelection: SoftwareSettingsMetadata[],
          ) => {
            if (!sourcePath) return [];
            if (!currentSelection) return resolvedSelection;

            const normalizedSourcePath = normalizePath(sourcePath);
            const currentSelectionPath = toFullPath(currentSelection.location, currentSelection.name);

            // Only upgrade the original duplicated selection from fallback metadata.
            // If the user already picked a different file while async data was loading, keep it.
            return currentSelectionPath === normalizedSourcePath ? resolvedSelection : [currentSelection];
          };

          const preserveManySelection = (
            sourcePaths: string[] | null | undefined,
            currentSelection: SoftwareSettingsMetadata[] | null,
            resolvedSelection: SoftwareSettingsMetadata[],
          ) => {
            if (!sourcePaths?.length) return [];
            if (!currentSelection?.length) return resolvedSelection;

            const normalizedSourcePaths = sourcePaths.map((path) => normalizePath(path)).sort();
            const currentSelectionPaths = currentSelection.map((setting) => toFullPath(setting.location, setting.name)).sort();

            // Only replace the selection when it still matches the duplicated source values.
            // Otherwise preserve any user changes made after the duplicate form opened.
            return JSON.stringify(currentSelectionPaths) === JSON.stringify(normalizedSourcePaths)
              ? resolvedSelection
              : currentSelection;
          };

          const resolveSingleSetting = (
            path: string | null | undefined,
            list: SoftwareSettingsMetadata[],
            settingsType: SettingsTypeEnumSchema,
          ) => {
            if (!path) return [];

            const normalizedPath = normalizePath(path);
            const match = list.find((item) => toFullPath(item.location, item.name) === normalizedPath);
            return [match ?? toFallbackSetting(path, job.createdBy ?? "", job.softwareVersion ?? "", settingsType)];
          };

          const resolveManySettings = (
            paths: string[] | null | undefined,
            list: SoftwareSettingsMetadata[],
            settingsType: SettingsTypeEnumSchema,
          ) => (paths ?? []).map((path) => {
            const normalizedPath = normalizePath(path);
            return list.find((item) => toFullPath(item.location, item.name) === normalizedPath)
              ?? toFallbackSetting(path, job.createdBy ?? "", job.softwareVersion ?? "", settingsType);
          });

          jobSetupStore.setDirectDIASettings(
            preserveSingleSelection(
              job.softwareSettingsFile,
              jobSetupStore.settingsDirectDIA,
              resolveSingleSetting(job.softwareSettingsFile, experimentResponse.list ?? [], SettingsTypeEnumSchema.Experiment),
            ),
          );
          jobSetupStore.setConditionSetup(
            preserveSingleSelection(
              job.conditionSetupFile,
              jobSetupStore.conditionSetup,
              resolveSingleSetting(job.conditionSetupFile, conditionResponse.list ?? [], SettingsTypeEnumSchema.ConditionSetup),
            ),
          );
          jobSetupStore.setModificationRepository(
            preserveSingleSelection(
              job.modificationRepositoryFile,
              jobSetupStore.modificationRepository,
              resolveSingleSetting(job.modificationRepositoryFile, modificationResponse.list ?? [], SettingsTypeEnumSchema.Modification),
            ),
          );
          jobSetupStore.setReportSettings(
            preserveManySelection(
              job.reportSettingsFile,
              jobSetupStore.reportSettings,
              resolveManySettings(job.reportSettingsFile, reportResponse.list ?? [], SettingsTypeEnumSchema.Report),
            ),
          );
          jobSetupStore.setGOSettings(
            preserveManySelection(
              job.goFile,
              jobSetupStore.goSettings,
              resolveManySettings(job.goFile, goResponse.list ?? [], SettingsTypeEnumSchema.GO),
            ),
          );
        }
      } catch (error) {
        console.error("Failed to load job for duplication", error);
      }
    };

    loadJob();

    return () => {
      isCancelled = true;
    };
  }, [loadExistingJobForRerun, location.search, resetCreateJobState]);

  return (
    <div className="fill-all">
        <Card className="fill-all">
          <CardHeader>
          <CardTitle style={{ fontWeight: "bold" }}>{location.search.includes("rerunJobId=") ? "Duplicate Job" : "New Job"}</CardTitle>
          </CardHeader>
          <JobSetupForm />
        </Card>
    </div>
  );
};

/**
 * Job setup page where new jobs can be created using a form.
 * @returns Functional component
 */
const JobSetupPage: React.FC = () => {
  const [panes, setPanes] = React.useState([
    { size: "30%", min: "15%" }, // Left pane
    { min: "15%" }, // Right pane
  ]);


  const libraryRepository = useInjection(LibraryRepository);
  const archiveRepository = useInjection(ArchiveRepository);

  const [availableLibraries, setAvailableLibraries] = React.useState<FileMetadata[]>([]);
  const [isLibrariesLoading, setIsLibrariesLoading] = React.useState<boolean>(false);
  const [availableArchiveJobs, setAvailableArchiveJobs] = React.useState<ArchiveGridItem[] | undefined>(undefined);
  const [isArchivesLoading, setIsArchivesLoading] = React.useState<boolean>(false);

  const { pipelineType, rightPanelTab, setRightPanelTab, resumeFromStepId } = useJobSetupStore((state) => ({
    pipelineType: state.CreateJobState.pipelineType,
    rightPanelTab: state.rightPanelTab,
    setRightPanelTab: state.setRightPanelTab,
    resumeFromStepId: state.CreateJobState.resumeFromStepId,
  }));
  const isRerunMode = React.useMemo(() => new URLSearchParams(location.search).has("rerunJobId"), [location.search]);
  const showRawFilesTab = !isRerunMode || resumeFromStepId === PULSAR_STEP_1_ID;
  const showArchiveTabForRerun = !isRerunMode || resumeFromStepId === PULSAR_STEP_1_ID || resumeFromStepId === LIBRARY_GENERATION_ID;

  const handleTabSelect = (e: TabStripSelectEventArguments) => {
    setRightPanelTab(e.selected);
  };

  const showLibraryTab = pipelineType === JobPipelineTypeSchema.LibraryBased;
  const showArchiveTab = (pipelineType === JobPipelineTypeSchema.DirectDIA || pipelineType === JobPipelineTypeSchema.LibraryGeneration) && showArchiveTabForRerun;

  // If pipeline changes and hides the current tab, switch to first tab
  React.useEffect(() => {
    const tabCount = (showRawFilesTab ? 1 : 0) + (showArchiveTab ? 1 : 0) + (showLibraryTab ? 1 : 0);
    
    if (rightPanelTab >= tabCount) {
      setRightPanelTab(0);
    }
  }, [showLibraryTab, showArchiveTab, showRawFilesTab, rightPanelTab]);

  const fetchLibraries = async () => {
    setIsLibrariesLoading(true);
    try {
      const response = await libraryRepository.getLibraries();
      if (Array.isArray(response)) {
        setAvailableLibraries(response);
      } else if ((response as any).data && Array.isArray((response as any).data)) {
        setAvailableLibraries((response as any).data);
      } else {
        setAvailableLibraries([]);
      }
    } catch (error) {
      console.error("Error fetching libraries:", error);
    } finally {
      setIsLibrariesLoading(false);
    }
  };

  const fetchArchiveJobs = async () => {
    setIsArchivesLoading(true);
    try {
      const jobs = await archiveRepository.getArchiveCandidateJobs();
      
      if (Array.isArray(jobs) && jobs.length > 0) {
        const jobItems: ArchiveGridItem[] = jobs.map((job) => ({
                id: job.id,
                name: job.name || job.id,
                path: job.archivesPath || job.id,
                isFolder: true,
                createdAt: job.createdAt,
                jobCreatedAt: job.createdAt,
                jobAnalysisName: job.analysisName || undefined
            }));
        
        setAvailableArchiveJobs(jobItems);
      } else {
        setAvailableArchiveJobs([]);
      }
    } catch (error) {
      console.error("Error fetching archive jobs:", error);
      setAvailableArchiveJobs([]);
    } finally {
      setIsArchivesLoading(false);
    }
  };

  // Preload libraries and archives on mount, but wait for idle time to avoid blocking initial render
  React.useEffect(() => {
    const loadData = () => {
      fetchLibraries();
      // Only fetch archives if pipeline type requires it? 
      // The user said "start loading even before clicking on that tab".
      // So we fetch it regardless, or if the pipeline type *could* show it.
      // Since pipelineType defaults to DirectDIA, it is likely shown.
      fetchArchiveJobs();
    };

    // Use requestIdleCallback if available to load data when the main thread is free
    if ('requestIdleCallback' in window) {
      const handle = (window as any).requestIdleCallback(loadData);
      return () => (window as any).cancelIdleCallback(handle);
    } else {
      // Fallback for browsers that don't support requestIdleCallback
      const timeout = setTimeout(loadData, 1000);
      return () => clearTimeout(timeout);
    }
  }, []);



  const onChange = (event: SplitterOnChangeEvent) => {
    setPanes(event.newState as Array<{ size: string; min: string }>);
  };

  return (
    <div style={{ height: "100%", overflow: "hidden" }}>
      <Splitter
        style={{ height: "100%", width: "100%" }}
        orientation="horizontal"
        panes={panes}
        onChange={onChange}
      >
        <SplitterPane orientation="vertical" overlay={false} containsSplitter={false}>
          <CreateJobFormCard />
        </SplitterPane>
        <SplitterPane orientation="vertical" overlay={false} containsSplitter={false}>
          <div className="fill-all">
            <Card className="fill-all" style={{ display: "flex", flexDirection: "column" }}>
              <CardHeader style={{ flex: "0 0 auto" }}>
                <CardTitle style={{ fontWeight: "bold" }}>Data Explorer</CardTitle>
              </CardHeader>
              <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                <TabStrip id="ms-run-selection-tabs" selected={rightPanelTab} onSelect={handleTabSelect} style={{ height: "100%" }}>
                  {showRawFilesTab && (
                    <TabStripTab title="Raw Files">
                      <div style={{ height: "100%", padding: "10px", boxSizing: "border-box" }}>
                        <FileExplorerGrid select={true} />
                      </div>
                    </TabStripTab>
                  )}
                  {showArchiveTab && (
                    <TabStripTab title={
                      isArchivesLoading
                        ? <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>Archives <Loader size="small" type="infinite-spinner" /></span>
                        : "Archives"
                    }>
                      <div style={{ height: "100%", padding: "10px", boxSizing: "border-box" }}>
                        <ArchiveExplorerGrid 
                            onLoadingChange={setIsArchivesLoading} 
                            preloadedItems={availableArchiveJobs}
                        />
                      </div>
                    </TabStripTab>
                  )}
                  {showLibraryTab && (
                    <TabStripTab title={
                      isLibrariesLoading
                        ? <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>Libraries <Loader size="small" type="infinite-spinner" /></span>
                        : "Libraries"
                    }>
                      <div style={{ height: "100%", padding: "10px", boxSizing: "border-box" }}>
                        <LibraryExplorerGrid
                          libraries={availableLibraries}
                          loading={isLibrariesLoading}
                          onReload={fetchLibraries}
                        />
                      </div>
                    </TabStripTab>
                  )}
                </TabStrip>
              </div>
            </Card>
          </div>
        </SplitterPane>
      </Splitter>

    </div>
  );
};

export default JobSetupPage;
